import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { dialogs, i18next, site, siteLocales } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import saveSite from 'src/js/modules/saving/helpers/save-site';
import BulkEditingMoveToDialog from '../components/bulk-editing-move-to-dialog';
import buildFolderOptions from '../helpers/build-folder-options';
import buildFoldersNestingLevelMap from '../helpers/build-folders-nesting-level-map';
import getDisabledMoveTargetFolderIds from '../helpers/get-disabled-move-target-folder-ids';
import getFolderWithAncestorIds from '../helpers/get-folder-with-ancestor-ids';
import { ROOT_ID } from '../constants/index.js';

const { useForm } = reactHookForm;
const FIELD_NAME = 'folderToMove';

const getCaptions = () => ({
  title: i18next.t('Move to'),
  controlLabel: i18next.t('Folder'),
  noResultsText: i18next.t('No result'),
  placeholder: i18next.t('Select a folder'),
  errorMessage: i18next.t('Please select a folder'),
  submit: i18next.t('Submit'),
  cancel: i18next.t('Cancel'),
});

function BulkEditingMoveToDialogContainer({
  pageIds,
  folderIds,
  bulkEditing,
  itemsCount,
  resolveSharedData,
  onDialogMainAction,
  onDialogCancel,
}) {
  const dispatch = useDispatch();
  const pages = site.accessors.getPages();
  const folders = useSelector(site.selectors.getFolders);
  const currentLocale = useSelector(siteLocales.selectors.getCurrentLocale);
  const localizedFolders = folders.filter((folder) => folder.locale === currentLocale);
  const captions = getCaptions();
  const disabledFolderIds = getDisabledMoveTargetFolderIds({
    folderIds,
    pageIds,
    folders: localizedFolders,
    pages,
  });
  const folderOptions = buildFolderOptions(localizedFolders).map((option) =>
    disabledFolderIds.has(option.value) ? { ...option, disabled: true } : option
  );
  const foldersNestingLevelMap = buildFoldersNestingLevelMap(localizedFolders);

  const { control, handleSubmit: manageSubmit } = useForm({
    defaultValues: {
      [FIELD_NAME]: null,
    },
  });

  const handleCancel = () => {
    onDialogCancel({ bulkEditing, itemsCount });
    dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
  };

  const handleSubmit = (values) => {
    const selectedFolder = folderOptions.find((option) => option.value === values[FIELD_NAME]);
    const targetParentFolderId = selectedFolder.value === ROOT_ID ? null : selectedFolder.value;

    if (folderIds.length) {
      const selectedFolderIdsSet = new Set(folderIds);
      const foldersById = new Map(localizedFolders.map((folder) => [folder.id, folder]));
      const pagesById = new Map(pages.map((page) => [page.id, page]));

      const topLevelFolderIds = folderIds.filter(
        (id) => !selectedFolderIdsSet.has(foldersById.get(id)?.parentFolderId)
      );
      const topLevelPageIds = pageIds.filter(
        (id) => !selectedFolderIdsSet.has(pagesById.get(id)?.parentFolderId)
      );

      [...topLevelFolderIds, ...topLevelPageIds].forEach((itemId) => {
        dispatch(site.actions.moveToFolder(itemId, targetParentFolderId));
      });
    } else {
      pageIds.forEach((pageId) => {
        dispatch(site.actions.moveToFolder(pageId, targetParentFolderId));
      });
    }

    if (targetParentFolderId) {
      const folderIdsToOpen = getFolderWithAncestorIds(targetParentFolderId, localizedFolders);

      resolveSharedData((currentData) => {
        const treeState = currentData.pageManagerTreeState || {};

        return {
          ...currentData,
          pageManagerTreeState: {
            ...treeState,
            openNodeIds: [...new Set([...(treeState.openNodeIds || []), ...folderIdsToOpen])],
            scrollToItemId: targetParentFolderId,
          },
        };
      });
    }

    onDialogMainAction({ bulkEditing, itemsCount });
    saveSite();
    dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
  };

  return (
    <BulkEditingMoveToDialog
      fieldName={FIELD_NAME}
      control={control}
      captions={captions}
      folderOptions={folderOptions}
      folders={localizedFolders}
      foldersNestingLevelMap={foldersNestingLevelMap}
      onSubmit={manageSubmit(handleSubmit)}
      onCancel={handleCancel}
    />
  );
}

BulkEditingMoveToDialogContainer.propTypes = {
  pageIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  folderIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  bulkEditing: PropTypes.bool.isRequired,
  itemsCount: PropTypes.number.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default BulkEditingMoveToDialogContainer;
